import * as StringUtils from '@packages/utilities/string-utils';
import {trim, startsWith} from 'lodash';
import {test} from '@packages/utilities/fp/utils';
import {sanitizeProp} from '@packages/utilities/react-sanitize';
import {
  Environment,
  WebflowEnvironments,
} from '@packages/systems/core-constants';

const ALLOWED_HREF_PREFIXES = [
  '/',
  '#',
  'mailto:',
  'tel:',
  'sms:',
  'http://',
  'https://',
  'ftp://',
  'file://',
  'news://',
];

export const INTERNAL_ATTRS = new Set<string>(['data-ix', 'data-w-tab']);

export const RESERVED_ATTRS = new Set<string>([
  'abbr',
  'accept',
  'action',
  'align',
  'alt',
  // 'autocomplete',
  'autofocus',
  'autoplay',
  'bgcolor',
  'border',
  'char',
  'charoff',
  'charset',
  // 'checked',
  // 'cite',
  // 'class',
  'cols',
  'colspan',
  'content',
  'controls',
  'coords',
  'crossorigin',
  'datetime',
  'default',
  'disabled',
  'download',
  // 'for',
  'form',
  'formaction',
  'formenctype',
  'formmethod',
  'formnovalidate',
  'formtarget',
  'frame',
  'group',
  'headers',
  'height',
  'high',
  'href',
  // 'hreflang',
  'hspace',
  'icon',
  'id',
  'ismap',
  'kind',
  'label',
  // 'lang',
  'list',
  'loop',
  'low',
  'manifest',
  // 'max',
  // 'maxlength',
  'media',
  'method',
  // 'min',
  'multiple',
  'muted',
  'name',
  // 'novalidate',
  'optimum',
  // 'pattern',
  'placeholder',
  'poster',
  'preload',
  'radiogroup',
  'readonly',
  'ref',
  // 'rel',
  'required',
  'reversed',
  'rows',
  'rowspan',
  'sandbox',
  'scheme',
  'scope',
  'scoped',
  'scrolling',
  'seamless',
  'selected',
  'shape',
  'size',
  'sizes',
  'src',
  'srcdoc',
  'srclang',
  // 'step',
  'style',
  // 'target',
  // 'title',
  'type',
  // 'usemap',
  'value',
  'vspace',
  'width',
  'wrap',
  'xmlns',

  // Events
  'onblur',
  'onclick',
  'ondblclick',
  'ondrag',
  'ondrop',
  'onerror',
  'onfocus',
  'onfocusin',
  'onfocusout',
  'onhelp',
  'oninput',
  'onkeydown',
  'onkeypress',
  'onkeyup',
  'onload',
  'onmousedown',
  'onmouseenter',
  'onmouseleave',
  'onmousemove',
  'onmouseout',
  'onmouseover',
  'onmouseup',
  'onmousewheel',
  'onpaste',
  'onredo',
  'onscroll',
  'onsubmit',
  'onundo',

  // React internals
  'dangerouslysetinnerhtml',
  'key',
  // 'is' is a special custom element attribute that makes it possible to by-pass React's sanitization.
  // Thus we disallow using it because it can be used in XSS attacks.
  'is',
]);

const testForScript = test(/<\/?\s*script\b/);

/**
 * Basic check to see if a block of HTML has a script tag.
 */
export const hasScript = (html: string) => {
  if (!html) {
    return false;
  }
  return testForScript(html.toLowerCase());
};

/**
 * Appends http:// to links that appear to need it.
 */
export const cleanHref = (href: string) => {
  if (!href) {
    return href;
  }

  href = trim(href);

  // Allow common scripts
  /* eslint-disable no-script-url */
  if (href === 'javascript:void(0);' || href === '#') {
    return href;
  }
  /* eslint-enable */

  // Special flag for allowing any type of href
  if (startsWith(href, '`') && !hasScript(href)) {
    return trim(href, '`');
  }

  const validPrefix = ALLOWED_HREF_PREFIXES.find((prefix) =>
    startsWith(href.toLowerCase(), prefix)
  );

  if (!validPrefix) {
    href = 'https://' + href;
  }

  return href;
};

const DISALLOWED_ARBITRARY_JS_ENVIRONMENTS: Environment[] = [
  WebflowEnvironments.DESIGNER,
];

/**
 * Sanitizes input href to disallow arbitrary JS in some environments for security reasons
 */
export const hrefAttr = (href: string, runtimeEnvironment: Environment) => {
  return typeof href === 'string' &&
    DISALLOWED_ARBITRARY_JS_ENVIRONMENTS.includes(runtimeEnvironment)
    ? {href: sanitizeProp('href', href)}
    : {href};
};

/**
 * Converts a string to something that is usable as a DOM id.
 * @param  {String} string The original string.
 * @param  {String} prefix An optional prefix.
 * @return {String}        A clean string ID.
 */
export function toStringId(string: string, prefix?: string) {
  const clean = trim(trim(StringUtils.clean(string), ' _-'), '.');
  const lowercase = clean.toLowerCase();

  const valid: Array<any | string> = [];

  for (let i = 0, j = clean.length; i < j; i++) {
    const code = lowercase.charCodeAt(i);

    // 0-9 A-Z - _ . [ ]
    if (
      (code >= 48 && code <= 57) ||
      (code >= 97 && code <= 122) ||
      code === 45 ||
      code === 95 ||
      code === 46 ||
      code === 91 ||
      code === 93
    ) {
      valid.push(clean.charAt(i));
    } else {
      valid.push(' ');
    }
  }

  if (valid.length === 0) {
    return null;
  }

  let id = trim(StringUtils.clean(valid.join('')).replace(/\s/g, '-'), ' _-');

  if (id === '') {
    return null;
  }

  // Add optional prefix
  if (prefix) {
    id = trim(prefix, ' -') + '-' + id;
  }

  return id;
}

/**
 * Creates a id to a "safe" form id. Makes it so that the form name doesn't contain a $ or ., so it doesn't cause issues when persisting to Mongo
 * @param  {String} id Non-safe ID
 * @return {String}    Safe ID
 */
export function toSafeFormId(id: string) {
  const safeId = trim(
    trim(StringUtils.clean(id.replace(/(\$|\.)/g, ' ')), '-_')
  );

  return safeId;
}

/**
 * Checks whether a DOM ID is reserved and cannot be set in the UI.
 * @param  {String}  id The user-supplied ID.
 * @return {Boolean}    True if the ID is not allowed by Webflow.
 */
export function isReservedId(id: string) {
  if (!id) {
    return false;
  }

  const cleanId = trim(id);

  // Don't allow some core JS node properties
  // This is to make sure that people don't end up with <input id="id"> inside a form,
  // which most browsers extend the native node object with for "convenience"
  // e.g. document.myform.id no longer refers to the form's "id" attribute, but rather to the id <input>
  if (
    cleanId === 'id' ||
    cleanId === 'attributes' ||
    cleanId === 'innerHTML' ||
    cleanId === 'innerText' ||
    cleanId === 'nodeName' ||
    cleanId === 'nodeType' ||
    cleanId === 'style'
  ) {
    return true;
  }

  // Don't allow core webflow tags to be overwritten (only important when a <form> element has children that have a matching id)
  if (startsWith(cleanId, '__wf_')) {
    return true;
  }

  return false;
}

/**
 * Indicates whether the name of an attribute is valid.
 * @param  {String}  name The user-entered name.
 * @return {Boolean}      True if the name is alphanumeric and doesn't start with a number.
 */
export function isValidAttrName(name: string) {
  if (!name || !isString(name)) {
    return false;
  }
  return /^[a-zA-Z_][-a-zA-Z0-9_:]+$/g.test(name);
}

/**
 * Indicates whether the attribute value is to spec. http://www.w3.org/TR/html-markup/syntax.html#syntax-attribute-value
 * @param  {String}  value  The user-entered value.
 * @return {Boolean}        True if the attribute value doesn't
 */
export function isValidAttrValue(value: string) {
  if (!isString(value)) {
    return false;
  }

  // Since Webflow surrounds attributes with double quotes, we can't have double quotes inside
  if (value.indexOf('"') >= 0) {
    return false;
  }

  return true;
}

/**
 * Determines if an attribute is internally reserved by webflow.
 * @param  {String}  name The name of an attribute.
 * @return {Boolean}      True if it's used by some native HTML5 tag.
 */
export function isInternalAttr(name: string) {
  if (!name || !isString(name)) {
    return false;
  }
  name = name.toLowerCase().trim();
  return INTERNAL_ATTRS.has(name);
}

/**
 * Determines if an attribute is reserved by HTML4/5.
 * TODO: Make this tagname-specific.
 * @param  {String}  name The name of an attribute.
 * @return {Boolean}      True if it's used by some native HTML5 tag.
 */
export function isReservedAttr(name: string) {
  if (!name || !isString(name)) {
    return false;
  }
  name = name.toLowerCase().trim();
  return RESERVED_ATTRS.has(name);
}

const isString = (stringVal: string): boolean => {
  return typeof stringVal === 'string';
};
